import { Component, Input, OnInit, inject, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { Constants } from 'src/app/models/constants';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { FormUtilService } from 'src/app/services/util/form-util.service';
import { ErrorDialogComponent } from '../../error-dialog/error-dialog.component';
import { finalize, map, Observable } from 'rxjs';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-add-enc-key',
    templateUrl: './add-enc-key.component.html',
    styleUrls: ['./add-enc-key.component.scss'],
    standalone: false
})
export class AddEncKeyComponent  implements OnInit {
private destroyRef = inject(DestroyRef);
  @Input() id;
  @Input() value;
  @Input() edit;
  encKeyForm: FormGroup;
  Constants = Constants;
  showLoader = false;
  minExpDate: Date;
  isLoading: boolean;
  isFormChanged = false;
  originalEditData: any;
  constructor(public datePipe: DatePipe, public sidenavService: SidenavService, private formBuilder: FormBuilder, private instanceService: InstancesService,
    private toastrService: CustomToastrService, private dialog: MatDialog, private toastr: ToastrService) {
    
  }

  ngOnInit(): void {
    this.createForm();
    const date = new Date();
    this.minExpDate = new Date();
    this.minExpDate.setDate(date.getDate() + 1);
    if(this.value) {
      this.encKeyForm.setValue({name: this.value?.name, publicKey: this.value.publicKey, expirationDate: this.value.expirationDate});
    }
    // Store original data for change tracking
    this.originalEditData = {
      name: this.value?.name,
      publicKey: this.value?.publicKey,
      expirationDate: this.value?.expirationDate
    };
    // Subscribe to changes
    this.encKeyForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkFormChanges());
  }

  checkFormChanges(): void {
    const currentData = this.encKeyForm.getRawValue();
    this.isFormChanged = JSON.stringify({
      name: currentData.name,
      publicKey: currentData.publicKey,
      expirationDate: currentData.expirationDate
    }) !== JSON.stringify(this.originalEditData);
  }

  closeSidenav(): void {
    this.sidenavService.closeRightSidenav();
  }

  createForm(): void {
    const formConfig: any = {
      name: ['', [Validators.required,Validators.pattern('^[A-Za-z0-9]*$'),Validators.maxLength(16)]],
      publicKey: ['', [Validators.required]],
      expirationDate: [undefined, [Validators.required]]
    };
    this.encKeyForm = this.formBuilder.group(formConfig);
  }

  validateForm(): any {
    if(!this.edit) {
    this.isKeyNameInvalid()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(invalid => {
        if (invalid) {
          this.setErrorToNameControl();
        } else {
          if (!FormUtilService.validateForm(this.encKeyForm)) {
            return;
          }
          this.addKey();
        }
      })
    } else {
      if(this.encKeyForm.get('expirationDate').value < new Date()) {
        this.setErrorToExpControl();
        return;
      }
      this.editKey();
    }
  }

  private setErrorToExpControl(): void {
    setTimeout(() => this.encKeyForm.get('expirationDate').setErrors({greaterThanToday: true}));
  }

  private isKeyNameInvalid(): Observable<boolean> {
    return this.instanceService.getEncryptionKeys(this.id)
      .pipe(
        finalize(() => this.isLoading = false),
        map((results) => {
          const currentKeyName = this.encKeyForm.get('name').value?.toLowerCase();
          return results.some(key => key?.name?.toLowerCase() === currentKeyName);
        }));
  }

  private setErrorToNameControl(): void {
    setTimeout(() => this.encKeyForm.get('name').setErrors({nameNotUnique: true}));
  }


  addKey(): void {
  const data = this.encKeyForm.getRawValue();
  this.dialog.open(ConfirmationDialogComponent, {
    panelClass: 'default-dialog-wrapper',
    width: '566px',
    autoFocus: false,
    data: {
      title: translate('INSTANCE_DETAILS.ADD_KEY_TITLE'),
      message:translate('INSTANCE_DETAILS.ADD_KEY_MESSAGE'),
      confirmButtonText: translate('BUTTONS.CONFIRM')
    }
  })
    .afterClosed()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(confirm => {
      if (confirm) {
        this.showLoader = true;
        data.id = this.id;
        data.publicKey = btoa(data.publicKey),
        data.expirationDate = this.datePipe.transform(data.expirationDate, 'yyyy-MM-dd');
        this.instanceService.postEncryptionKey(data)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(response => {
            this.showLoader = false;
            this.sidenavService.closeRightSidenav(true);
            setTimeout(() => {
              const title = translate('INSTANCE_DETAILS.KEYS'); 
              const message = translate('INSTANCE_DETAILS.ADD_PGP_KEY_SUCCESS');
              this.toastrService.successMessage(message, title);
            }, 500)
          }, (error) => {
            this.showLoader = false;
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.dialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '630px',
              panelClass: 'flex-dialog',
              data: {
                title:  translate('ERROR_DIALOG.ADD_KEY_FAILED'),
                description: errorMessage
              }
            });
          })
      }
    })
  }

  editKey(): void {
    const data = this.encKeyForm.getRawValue();
    this.dialog.open(ConfirmationDialogComponent, {
      panelClass: 'default-dialog-wrapper',
      width: '566px',
      autoFocus: false,
      data: {
        title: translate('INSTANCE_DETAILS.EDIT_KEY_TITLE'),
        message:translate('INSTANCE_DETAILS.EDIT_KEY_MESSAGE'),
        confirmButtonText: translate('BUTTONS.CONFIRM')
      }
    })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm) {
          this.showLoader = true;
          const editData = {
            id: this.id,
            keyName: data.name,
            publicKey: btoa(data.publicKey),
            expirationDate: this.datePipe.transform(data.expirationDate, 'yyyy-MM-dd')
          }
          this.instanceService.updateEncryptionKey(editData)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
              this.showLoader = false;
              this.sidenavService.closeRightSidenav(true);
            setTimeout(() => {
              const message = translate('INSTANCE_DETAILS.EDIT_PGP_KEY_SUCCESS');
              const title = translate('INSTANCE_DETAILS.KEYS'); 
              this.toastrService.successMessage(message, title);
            }, 500)
            }, (error) => {
              this.showLoader = false;
              const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
              this.dialog.open(ErrorDialogComponent, {
                minHeight: '342px',
                width: '630px',
                panelClass: 'flex-dialog',
                data: {
                  title:  translate('ERROR_DIALOG.ADD_KEY_FAILED'),
                  description: errorMessage
                }
              });
            })
        }
      })
    }
}
